import { useMemo } from 'react';
import { useAppSelector } from '@core/store/hooks';
import { selectCurrentUser } from '../store/authSlice';
import { hasPermission, hasGroup, hasAnyGroup, GROUPS } from '../utils/permissionRules';

/**
 * Universal permission hook with minimal API
 *
 * This hook provides a clean, unified interface for all permission checks in the application.
 * It automatically handles the permission hierarchy (superadmin > admin > managers).
 *
 * @returns {Object} Permission API
 * @returns {Function} can - Check permission with hierarchy (e.g., can(PERMISSIONS.MANAGE_EMERGENCY))
 * @returns {Function} hasRole - Check specific role/group (e.g., hasRole(GROUPS.SUPERADMIN))
 * @returns {Function} hasAnyRole - Check if has any of the roles (e.g., hasAnyRole([...]))
 * @returns {boolean} isSuperadmin - Quick check if user is superadmin
 * @returns {boolean} isAdmin - Quick check if user is admin
 * @returns {boolean} isStaff - Quick check if user is staff
 * @returns {Object|null} user - Current user object
 *
 * @example
 * // Basic usage
 * const { can } = usePermission();
 * if (can(PERMISSIONS.MANAGE_EMERGENCY)) {
 *   // Render admin content
 * }
 *
 * @example
 * // Role checks
 * const { isSuperadmin, isAdmin } = usePermission();
 * if (isSuperadmin) return <SuperadminPanel />;
 * if (isAdmin) return <AdminPanel />;
 *
 * @example
 * // Destructure only what you need
 * const { can, user } = usePermission();
 */
export function usePermission() {
  const user = useAppSelector(selectCurrentUser);

  const api = useMemo(() => ({
    /**
     * Check permission with automatic hierarchy
     * @param {Array<string>} permission - Permission constant from PERMISSIONS
     * @returns {boolean}
     */
    can: (permission) => hasPermission(user?.groups, permission),

    /**
     * Check if user has specific role/group
     * @param {string} groupName - Group name from GROUPS
     * @returns {boolean}
     */
    hasRole: (groupName) => hasGroup(user?.groups, groupName),

    /**
     * Check if user has any of the roles
     * @param {Array<string>} groupNames - Array of group names
     * @returns {boolean}
     */
    hasAnyRole: (groupNames) => hasAnyGroup(user?.groups, groupNames),

    /**
     * Quick permission checks
     */
    isSuperadmin: hasGroup(user?.groups, GROUPS.SUPERADMIN),
    isAdmin: hasGroup(user?.groups, GROUPS.ADMIN),
    isStaff: user?.is_staff ?? false,

    /**
     * Current user object
     */
    user,
  }), [user]);

  return api;
}
